import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Button } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { mergeApi, errMsg } from "../institutionMerge.api";
import {
  PAGE_LIMIT,
  normalizeReview,
  reviewParams,
} from "./split/reviewAdapter";
import { KIND_ORDER } from "./split/KindTag";
import { SECTIONS } from "./split/sections";
import { SummaryCard } from "./split/SummaryCard";
import { KindLegend, Section, SplitLayout, useWide } from "./split/SplitLayout";
import "./split/split.scss";

const DEBOUNCE_MS = 800;
const SEARCH_DEBOUNCE_MS = 350;
const CONCURRENCY = 4;

const NOTABLE_ORDER = [
  "merged",
  "deleted",
  "created",
  "modified",
  "archived",
  "id_replaced",
];

const codeIs = (it, ...codes) => !!it.action && codes.includes(it.action.code);

/**
 * Step 8 — the final check as a split view: for every entity the merge touches, what exists
 * today on the LEFT and what it will be with the operator's decisions on the RIGHT, field by
 * field, every ID replacement explicit. Fed by `GET /merge/:id/review` (one call per section,
 * records sections page by page); nothing is written. Reloaded when the decisions change.
 */
export function StepResult({ merge, preview, decisions }) {
  const mergeId = merge && merge._id;
  const p = preview || {};
  const existing = !!merge && merge.targetMode === "existing";
  const [summary, setSummary] = useState({
    loading: true,
    error: null,
    data: null,
  });
  const target =
    (summary.data && summary.data.target && summary.data.target.name) ||
    (existing && p.existingTarget && p.existingTarget.name) ||
    (decisions && decisions.targetName) ||
    (p.existingTarget && p.existingTarget.name) ||
    "la nouvelle institution";
  const decisionsKey = JSON.stringify(decisions || {});
  const wide = useWide();

  const [filter, setFilter] = useState("changed");
  const [q, setQ] = useState("");
  const [qd, setQd] = useState("");
  const [secs, setSecs] = useState({});
  const gen = useRef(0);
  const sumGen = useRef(0);
  // decisions seen by the last load of each part: a change of decisions reloads debounced, a filter / search immediately
  const summaryDecisions = useRef(decisionsKey);
  const sectionsDecisions = useRef(decisionsKey);
  // numbers of the summary tiles: computed from the unfiltered entity sections only
  const facts = useRef({});

  useEffect(() => {
    const t = setTimeout(() => setQd(q.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  const changedOnly = filter === "changed";
  const fetchSection = useCallback(
    (def, page) =>
      mergeApi
        .review(
          mergeId,
          reviewParams({
            section: def.key,
            q: qd,
            changedOnly,
            page: def.records ? page : undefined,
            limit: def.records ? PAGE_LIMIT : undefined,
          }),
        )
        .then(normalizeReview),
    [mergeId, qd, changedOnly],
  );

  const loadSummary = useCallback(async () => {
    if (!mergeId) return;
    const g = ++sumGen.current;
    setSummary(s => ({ ...s, loading: true, error: null }));
    try {
      const r = normalizeReview(
        await mergeApi.review(
          mergeId,
          reviewParams({ section: "summary", changedOnly: true }),
        ),
      );
      if (g === sumGen.current)
        setSummary({ loading: false, error: null, data: r.summary });
    } catch (e) {
      if (g === sumGen.current)
        setSummary(s => ({ ...s, loading: false, error: errMsg(e) }));
    }
  }, [mergeId]);

  const loadSection = useCallback(
    async (def, g) => {
      setSecs(prev => ({
        ...prev,
        [def.key]: { ...(prev[def.key] || {}), loading: true, error: null },
      }));
      try {
        const r = await fetchSection(def, 1);
        if (g !== gen.current) return;
        setSecs(prev => ({
          ...prev,
          [def.key]: {
            loading: false,
            error: null,
            items: r.items,
            total: r.total,
            page: 1,
          },
        }));
        if (r.summary)
          setSummary(s =>
            s.data ? s : { loading: false, error: null, data: r.summary },
          );
      } catch (e) {
        if (g !== gen.current) return;
        setSecs(prev => ({
          ...prev,
          [def.key]: {
            ...(prev[def.key] || {}),
            loading: false,
            error: errMsg(e),
          },
        }));
      }
    },
    [fetchSection],
  );

  const loadSections = useCallback(async () => {
    if (!mergeId) return;
    const g = ++gen.current;
    const queue = SECTIONS.slice();
    const worker = async () => {
      while (queue.length && g === gen.current)
        await loadSection(queue.shift(), g);
    };
    await Promise.all(Array.from({ length: CONCURRENCY }, worker));
  }, [mergeId, loadSection]);

  const more = async def => {
    const cur = secs[def.key];
    if (!cur || cur.loadingMore) return;
    const g = gen.current;
    setSecs(prev => ({ ...prev, [def.key]: { ...cur, loadingMore: true } }));
    try {
      const r = await fetchSection(def, (cur.page || 1) + 1);
      if (g !== gen.current) return;
      setSecs(prev => ({
        ...prev,
        [def.key]: {
          ...prev[def.key],
          loadingMore: false,
          items: prev[def.key].items.concat(r.items),
          total: r.total,
          page: r.page || (cur.page || 1) + 1,
        },
      }));
    } catch (e) {
      setSecs(prev => ({
        ...prev,
        [def.key]: { ...prev[def.key], loadingMore: false, error: errMsg(e) },
      }));
    }
  };

  // summary: on mount and when the decisions change
  useEffect(() => {
    const delay = summaryDecisions.current === decisionsKey ? 0 : DEBOUNCE_MS;
    summaryDecisions.current = decisionsKey;
    const t = setTimeout(loadSummary, delay);
    return () => clearTimeout(t);
  }, [loadSummary, decisionsKey]);
  // sections: on mount, on filter / search, and when the decisions change (debounced)
  useEffect(() => {
    const delay = sectionsDecisions.current === decisionsKey ? 0 : DEBOUNCE_MS;
    sectionsDecisions.current = decisionsKey;
    const t = setTimeout(loadSections, delay);
    return () => clearTimeout(t);
  }, [loadSections, decisionsKey]);

  /* ---------------------------------------------------------------- derived */

  const per = (summary.data && summary.data.perSection) || [];
  const nav = useMemo(() => {
    const rows = SECTIONS.map(def => {
      const ps = per.find(x => x.section === def.key);
      const st = secs[def.key];
      const loaded = st && !st.loading && !st.error;
      const kinds = ps
        ? ps.kinds
        : loaded
        ? KIND_ORDER.filter(k => st.items.some(i => i.kind === k))
        : [];
      return {
        key: def.key,
        label: def.label,
        changed: ps ? ps.changed : loaded ? st.total : null,
        total: ps ? ps.total : loaded ? st.total : 0,
        kinds,
      };
    });
    // « Résumé » is a plain anchor: no count, no dots
    return [
      {
        key: "summary",
        label: "Résumé",
        changed: undefined,
        total: 0,
        kinds: [],
      },
    ].concat(rows);
  }, [per, secs]);

  const entitiesLoaded = [
    "accounts",
    "addresses",
    "responsables",
    "blacklists",
  ].every(k => secs[k] && !secs[k].loading && !secs[k].error);
  if (!qd && entitiesLoaded) {
    const count = (k, pred) => secs[k].items.filter(pred).length;
    facts.current = {
      accounts: count("accounts", i => codeIs(i, "merge_account")),
      addresses: count(
        "addresses",
        i => codeIs(i, "address_merge") || !i.after.exists,
      ),
      responsables: count(
        "responsables",
        i => codeIs(i, "responsable_merge") || !i.after.exists,
      ),
      kept: count("blacklists", i => codeIs(i, "blacklist_keep")),
      dropped: count(
        "blacklists",
        i => codeIs(i, "blacklist_drop") || !i.after.exists,
      ),
    };
  }
  const recordsChanged = SECTIONS.filter(d => d.records).reduce((s, d) => {
    const ps = per.find(x => x.section === d.key);
    return s + (ps ? ps.changed : 0);
  }, 0);
  // the server's own counts (the ones its sentence uses) win over what the page derives
  const sf = summary.data && summary.data.facts;
  const allFacts = sf
    ? {
        sources: sf.institutionsArchived,
        records: sf.recordsMoved,
        accounts: sf.accountsMerged,
        addresses: sf.addressesMerged,
        responsables: sf.responsablesMerged,
        kept: sf.blacklistsKept,
        dropped: sf.blacklistsDropped,
        existing,
        loadingEntities: false,
      }
    : {
        sources: (p.sources || []).length,
        records: recordsChanged,
        existing,
        loadingEntities:
          !facts.current.accounts && facts.current.accounts !== 0,
        ...facts.current,
      };

  const isTargetItem = it =>
    it.model === "Institution" &&
    (it.isTarget ||
      (p.existingTarget && it.id === String(p.existingTarget._id)) ||
      (!existing && !it.before.exists));
  const targetBadge = it =>
    isTargetItem(it) &&
    !/nouvelle institution|qui reçoit/i.test(it.subtitle || "")
      ? existing
        ? "(institution qui reçoit)"
        : "nouvelle institution — créée en appliquant"
      : null;

  const sectionState = def => {
    const st = secs[def.key];
    if (def.records || !st || !st.items) return st;
    // what the operator decided comes first (merged, deleted, edited…), plain re-pointing last;
    // in « Établissements » the resulting institution leads
    const rank = it =>
      (isTargetItem(it) ? -1 : 0) * 100 +
      (NOTABLE_ORDER.indexOf(it.kind) + 1 || NOTABLE_ORDER.length + 1);
    const items = st.items
      .map((it, i) => ({ it, i }))
      .sort((a, b) => rank(a.it) - rank(b.it) || a.i - b.i)
      .map(x => x.it);
    return { ...st, items };
  };

  const archivedLine = (
    <div className="sv-archived">
      <span className="sv-archived-label">
        <InboxOutlined /> Archivées → {target} :
      </span>
      {(p.sources || []).map(s => (
        <span key={s._id} className="sv-archived-chip">
          {s.name}
        </span>
      ))}
    </div>
  );

  return (
    <SplitLayout
      wide={wide}
      nav={nav}
      filter={filter}
      onFilter={setFilter}
      q={q}
      onQ={setQ}
      top={
        <div id="sv-sec-summary" data-section="summary" className="sv-top">
          {summary.error && !summary.data && (
            <Alert
              type="error"
              showIcon
              message="Le contrôle final n'a pas pu être chargé"
              description={summary.error}
              action={
                <Button
                  size="small"
                  onClick={() => {
                    loadSummary();
                    loadSections();
                  }}
                >
                  Réessayer
                </Button>
              }
            />
          )}
          <SummaryCard
            sentence={summary.data && summary.data.sentence}
            target={target}
            facts={allFacts}
            loading={summary.loading && !summary.data}
            residual={(summary.data && summary.data.residual) || 0}
          />
          <KindLegend counts={summary.data && summary.data.counts} />
        </div>
      }
    >
      {SECTIONS.map(def => (
        <Section
          key={def.key}
          def={def}
          state={sectionState(def)}
          navInfo={nav.find(n => n.key === def.key)}
          wide={wide}
          target={target}
          q={qd}
          extra={def.key === "institutions" ? archivedLine : null}
          intro={
            def.key === "institutions"
              ? def.what(target, (p.sources || []).length)
              : undefined
          }
          targetBadge={def.key === "institutions" ? targetBadge : null}
          onMore={() => more(def)}
          onRetry={() => loadSection(def, gen.current)}
        />
      ))}
    </SplitLayout>
  );
}
